import * as React from "react";
import { useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import getUser from "../services/decodeJWT";
import ApiService from "../services/ApiService";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import Nav from "../components/Nav";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const Checkout = () => {

  const { id } = useParams();

  const [subscription, setSubscription] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  const navigate = useNavigate();
  const currentUser = getUser();

  const getSubscription = async () => {
    if (id) {
      setLoading(true);
      try {
        const response = await ApiService.get(`/cms/subscriptions/${id}`);
        setSubscription(response?.data || {});

        if(parseInt(response?.data?.total)==0){
          const isSuccess = await generateOrder({paymentIntent:{status:'succeeded'}}, currentUser, response?.data, id);
          isSuccess && (navigate('/alert', { state: { type: 'success', message: 'You are now subscribed!',  buttonText:'Go to Profile', buttonAction:'/profile' } })) || (<></>)
          
        }

        const paymentIntentResponse = await ApiService.post("/cms/stripe/payment-intent", {
          amount: (response?.data?.total * 100) || 100 ,
          currency: "usd",
          metadata: { subscriptionId: id, userId: currentUser?._id },
        });

        if (paymentIntentResponse?.clientSecret) {
          setClientSecret(paymentIntentResponse.clientSecret);
        } else {
          throw new Error("Failed to retrieve client secret.");
        }
      } catch (error) {
        console.error("Error fetching subscription data or Payment Intent:", error);
        setError("Failed to prepare checkout.");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    getSubscription();
  }, [id]);

  return (
    <div className="tyn-root dashboard">
      <Nav />
      <div className="tyn-content tyn-auth tyn-auth-centered">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-xl-6 col-lg-8">
              <div className="card border-0 py-4">
                <div className="my-3 text-center d-flex align-items-center justify-content-center">
                  <Link to="/" className="tyn-logo tyn-logo-sm">
                    <img
                      src="/images/logo/bardo-icon.png"
                      alt="Bardo logo"
                      className="logo-icon"
                    />
                    <img
                      src="/images/logo/bardo-text.png"
                      alt="Bardo logo"
                      className="logo-text"
                    />
                  </Link>
                </div>
                {clientSecret ? (
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <CheckoutForm clientSecret={clientSecret} subscription={subscription} currentUser={currentUser} id={id} />
                  </Elements>
                ) : (
                  error && <p className="text-danger small text-center">{error}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );


};

const generateOrder = async (result, user, subscription, id) => {

  const calculateExpirationDate = (type) => {
    const now = new Date();
    switch (type) {
      case "weekly":
        return new Date(now.setDate(now.getDate() + 7)).toISOString().slice(0, 10);
      case "monthly":
        return new Date(now.setMonth(now.getMonth() + 1)).toISOString().slice(0, 10);
      case "semiannual":
        return new Date(now.setMonth(now.getMonth() + 6)).toISOString().slice(0, 10);
      case "annual":
        return new Date(now.setFullYear(now.getFullYear() + 1)).toISOString().slice(0, 10);
      default:
        return now.toISOString().slice(0, 10);
    }
  }

  const orderData = {
    data: {
      name: subscription?.name,
      user_id: user?._id,
      user_name: user?.username,
      subscription_id: id,
      payment_id: result?.paymentIntent?.id,
      payment_method: result?.paymentIntent?.payment_method,
      status: result?.paymentIntent?.status,
      currency: result?.paymentIntent?.currency || 'usd',
      amount: parseFloat((result?.paymentIntent?.amount || subscription?.total) / 100)?.toFixed(2),
      expiring_at: calculateExpirationDate(subscription?.type)
    }
  };

  if (result?.paymentIntent?.status) {
    try {
      await ApiService.post(`/cms/orders`, orderData);
    } catch (error) {
      console.error("Error fetching subscription data or Payment Intent:", error);
      return false
    } finally {
      return orderData;
    }
  }
};

const CheckoutForm = ({ clientSecret, subscription, currentUser, id }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [paymentError, setPaymentError] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const navigate = useNavigate();

  const handleSubmit = async (event) => {
    event.preventDefault();
    setPaymentError(null);
    setIsProcessing(true);

    if (!stripe || !elements) {
      setPaymentError("Stripe is not loaded. Please try again later.");
      setIsProcessing(false);
      return;
    }

    const cardElement = elements.getElement(CardElement);

    try {
      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: { card: cardElement },
      });

      if (result.error) {
        setPaymentError(result.error.message);
      } else if (result.paymentIntent.status === "succeeded") {

        const isSuccess = generateOrder(result, currentUser, subscription, id);
        isSuccess && (navigate('/alert', { state: { type: 'success', message: 'You are now subscribed!',  buttonText:'Go to Profile', buttonAction:'/profile' } })) || (<></>)

      } else {
        setPaymentError("Payment failed. Please try again.");
      }
    } catch (error) {
      setPaymentError("An error occurred. Please try again.");
      console.error("Payment Error:", error);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <form className="p-4" onSubmit={handleSubmit}>
      <h3>Checkout</h3>
      <div className="row g-3 gx-4">
        <div className="col-12">
          <h5>User</h5>
          <ul className="list-group mb-3">
            <li className="list-group-item d-flex justify-content-between align-items-center">
              <span>Username:</span> {currentUser?.username || "Subscription"}
            </li>
          </ul>
          <h5>Order Summary</h5>
          <ul className="list-group">
            <li className="list-group-item d-flex justify-content-between align-items-center">
              <span>Name:</span> {subscription?.name || "Subscription"}
            </li>
            <li className="list-group-item d-flex justify-content-between align-items-center">
              <span>Plan:</span> {subscription?.type || "N/A"}
            </li>
            <li className="list-group-item d-flex justify-content-between align-items-center">
              <span>Price:</span> ${subscription?.price || "0.00"}
            </li>
            {subscription?.promotion && (
              <li className="list-group-item d-flex justify-content-between align-items-center">
                <span>Discount:</span> {subscription?.discount || "0"}%
              </li>
            )}
            <li className="list-group-item d-flex justify-content-between align-items-center">
              <strong>Total:</strong> <strong>${subscription.total || "0.00"}</strong>
            </li>
          </ul>
        </div>
        <div className="col-12">
          <h5>Payment Information</h5>
          <div className="form-control p-3">
            <CardElement />
          </div>
          {paymentError && <p className="text-danger small mt-3">{paymentError}</p>}
        </div>
        <div className="col-12">
          <button
            type="submit"
            className="btn btn-primary w-100"
            disabled={!stripe || isProcessing}
          >
            {isProcessing ? "Processing..." : "Complete Purchase"}
          </button>
        </div>
      </div>
    </form>
  );
};


export default Checkout;
